import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useListings } from '@/hooks/useListings';
import { Listing } from '@/types/listing';
import ListingFormModal from './components/ListingFormModal';
import AdminListingRow from './components/AdminListingRow';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import AdminPinGate from './components/AdminPinGate';

export default function AdminPage() {
  const { listings, addListing, updateListing, deleteListing } = useListings();
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<Listing | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');

  const filtered = listings.filter((l) =>
    l.address.toLowerCase().includes(search.toLowerCase()) ||
    l.city.toLowerCase().includes(search.toLowerCase()) ||
    l.type.toLowerCase().includes(search.toLowerCase())
  );

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleSave = async (data: Omit<Listing, 'id'>) => {
    if (editTarget) {
      await updateListing(editTarget.id, data);
      showToast('Listing updated successfully!');
    } else {
      await addListing({ ...data, images: [...(data.images ?? [])] });
      showToast('New listing added!');
    }
    // Reset both together to avoid stale closure issues
    setEditTarget(null);
    setShowForm(false);
  };

  const handleEdit = (listing: Listing) => {
    setEditTarget(listing);
    setShowForm(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteId) {
      deleteListing(deleteId);
      showToast('Listing deleted.');
      setDeleteId(null);
    }
  };

  const activeCount = listings.filter((l) => l.status === 'Active' || l.status === 'New').length;
  const pendingCount = listings.filter((l) => l.status === 'Pending').length;
  const soldCount = listings.filter((l) => l.status === 'Sold').length;

  return (
    <AdminPinGate>
      <div className="min-h-screen bg-stone-50">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-gray-500 hover:text-amber-600 cursor-pointer transition-colors">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </span>
              <span className="text-sm">Back to Website</span>
            </Link>
            <div className="w-px h-5 bg-gray-200"></div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 flex items-center justify-center bg-amber-500 rounded text-white">
                <i className="ri-home-2-line text-xs"></i>
              </span>
              <span className="font-bold text-gray-900 text-sm">Listings Manager</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">Amparo Jarquin</span>
            <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold">AJ</div>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-6 py-10">
          {/* Page Title */}
          <div className="flex items-start justify-between mb-8 flex-col sm:flex-row gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Listings</h1>
              <p className="text-gray-500 text-sm mt-1">Add, edit, and manage your Florida properties</p>
            </div>
            <button
              onClick={() => { setEditTarget(null); setShowForm(true); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line"></i>
              </span>
              Add Listing
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            {[
              { label: 'Active / New', value: activeCount, icon: 'ri-checkbox-circle-line', color: 'text-green-600', bg: 'bg-green-50' },
              { label: 'Pending', value: pendingCount, icon: 'ri-time-line', color: 'text-orange-500', bg: 'bg-orange-50' },
              { label: 'Sold', value: soldCount, icon: 'ri-trophy-line', color: 'text-amber-600', bg: 'bg-amber-50' },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-lg border border-gray-100 p-5 flex items-center gap-4">
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${stat.bg}`}>
                  <i className={`${stat.icon} ${stat.color} text-lg`}></i>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Search */}
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-md px-4 py-2.5 mb-6 focus-within:ring-2 focus-within:ring-amber-400 focus-within:border-transparent transition-all">
            <span className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by address, city, or type..."
              className="flex-1 outline-none text-sm text-gray-700 bg-transparent"
            />
            {search && (
              <button onClick={() => setSearch('')} className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-sm"></i>
              </button>
            )}
          </div>

          {/* Listings List */}
          {filtered.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-lg border border-gray-100">
              <div className="w-16 h-16 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-4">
                <i className="ri-home-3-line text-amber-400 text-2xl"></i>
              </div>
              <p className="text-gray-700 font-semibold mb-1">{search ? 'No results found' : 'No listings yet'}</p>
              <p className="text-gray-400 text-sm mb-5">
                {search ? 'Try a different search term' : 'Click "Add Listing" to add your first property'}
              </p>
              {!search && (
                <button
                  onClick={() => { setEditTarget(null); setShowForm(true); }}
                  className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
                >
                  Add Your First Listing
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-gray-400 text-xs mb-3">{filtered.length} listing{filtered.length !== 1 ? 's' : ''}</p>
              {filtered.map((listing) => (
                <AdminListingRow
                  key={listing.id}
                  listing={listing}
                  onEdit={handleEdit}
                  onDelete={(id) => setDeleteId(id)}
                />
              ))}
            </div>
          )}
        </main>

        {/* Modals */}
        {showForm && (
          <ListingFormModal
            listing={editTarget}
            onSave={handleSave}
            onClose={() => { setShowForm(false); setEditTarget(null); }}
          />
        )}
        {deleteId && (
          <DeleteConfirmModal
            onConfirm={handleDeleteConfirm}
            onCancel={() => setDeleteId(null)}
          />
        )}

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-5 py-3 rounded-full z-50 animate-fade-in">
            <span className="flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-amber-400"></i>
              {toast}
            </span>
          </div>
        )}
      </div>
    </AdminPinGate>
  );
}
